import React, { Component, ErrorInfo, ReactNode } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Alert,
  Container,
} from '@mui/material';
import {
  ErrorOutline as ErrorIcon,
  Refresh as RefreshIcon,
  Home as HomeIcon,
} from '@mui/icons-material';
import { commonTokens } from '../../../constants/themes/base';
import { notifySlack } from '../../../utils/slackErrorNotifier';
import { isChunkLoadError, reloadOnceForChunkError } from '../../../utils/chunkLoadRecovery';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
  isChunkRecovery: boolean;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, isChunkRecovery: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, isChunkRecovery: false };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    if (isChunkLoadError(error)) {
      this.setState({
        hasError: true,
        error,
        errorInfo,
        isChunkRecovery: true,
      });
      reloadOnceForChunkError();
      return;
    }

    this.setState({
      error,
      errorInfo,
    });

    // Log error to monitoring service
    this.logErrorToService(error, errorInfo);
  }

  private logErrorToService = (error: Error, errorInfo: ErrorInfo) => {
    console.error('Error Boundary caught an error:', error, errorInfo);

    void notifySlack(error, {
      source: 'react.error_boundary',
      component_stack: errorInfo.componentStack,
      digest: (error as Error & { digest?: string }).digest,
    });
  };

  private handleRetry = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined, isChunkRecovery: false });
  };

  private handleGoHome = () => {
    window.location.href = '/';
  };

  private handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      if (this.state.isChunkRecovery) {
        return (
          <Container maxWidth="sm" sx={{ py: 8 }}>
            <Box display="flex" alignItems="center" justifyContent="center" minHeight="50vh">
              <Alert severity="info" sx={{ width: '100%' }}>
                A new version is available. Refreshing...
              </Alert>
            </Box>
          </Container>
        );
      }

      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <Container maxWidth="md" sx={{ py: 8 }}>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            minHeight="60vh"
          >
            <Card
              sx={{
                maxWidth: 600,
                width: '100%',
                textAlign: 'center',
                boxShadow: commonTokens.shadows.xl,
                borderRadius: commonTokens.borderRadius.lg,
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    backgroundColor: `${commonTokens.colors.error}15`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 3,
                  }}
                >
                  <ErrorIcon
                    sx={{
                      fontSize: 40,
                      color: commonTokens.colors.error,
                    }}
                  />
                </Box>

                <Typography
                  variant="h4"
                  fontWeight={600}
                  color="text.primary"
                  gutterBottom
                >
                  Oops! Something went wrong
                </Typography>

                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ mb: 3, lineHeight: 1.6 }}
                >
                  We're sorry for the inconvenience. An unexpected error occurred.
                  Our team has been notified and is working to fix this issue.
                </Typography>

                {process.env.NODE_ENV === 'development' && this.state.error && (
                  <Alert
                    severity="error"
                    sx={{
                      mb: 3,
                      textAlign: 'left',
                      '& .MuiAlert-message': {
                        width: '100%',
                      },
                    }}
                  >
                    <Typography variant="body2" fontWeight={600} gutterBottom>
                      Error Details (Development Only):
                    </Typography>
                    <Typography
                      variant="body2"
                      component="pre"
                      sx={{
                        fontSize: '0.75rem',
                        overflow: 'auto',
                        maxHeight: 200,
                        fontFamily: 'monospace',
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      {this.state.error.message}
                      {this.state.errorInfo?.componentStack}
                    </Typography>
                  </Alert>
                )}

                <Box
                  display="flex"
                  gap={2}
                  justifyContent="center"
                  flexWrap="wrap"
                >
                  <Button
                    variant="contained"
                    startIcon={<RefreshIcon />}
                    onClick={this.handleRetry}
                    sx={{
                      backgroundColor: commonTokens.colors.primary,
                      '&:hover': {
                        backgroundColor: commonTokens.colors.primaryDark,
                      },
                    }}
                  >
                    Try Again
                  </Button>

                  <Button
                    variant="outlined"
                    startIcon={<HomeIcon />}
                    onClick={this.handleGoHome}
                    sx={{
                      borderColor: commonTokens.colors.primary,
                      color: commonTokens.colors.primary,
                      '&:hover': {
                        borderColor: commonTokens.colors.primaryDark,
                        backgroundColor: `${commonTokens.colors.primary}08`,
                      },
                    }}
                  >
                    Go Home
                  </Button>

                  <Button
                    variant="text"
                    onClick={this.handleReload}
                    sx={{
                      color: commonTokens.semantic.textSecondary,
                      '&:hover': {
                        backgroundColor: `${commonTokens.semantic.textSecondary}08`,
                      },
                    }}
                  >
                    Reload Page
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Container>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary; 